import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, DataTable, DataTableContainer, InfiniteScroll, SimpleLabel, Text } from '@hopskipdrive/react-components';
import { variables } from '@rideiq/shared/components/variables';
import startCase from 'lodash/startCase';

import { useInfiniteTableScroll } from '../../../hooks/useInfiniteTableScroll';
import { useTimezone } from '../../../hooks/useTimezone';
import { useNavigation } from '../../../shared/hooks/useNavigation';
import { formatPhoneParenthesis } from '../../../utils/helpers';
import { Organizer } from '../../types';

import { useOrganizers } from './useOrganizers';

export const UsersTable = () => {
  const { navigateTo } = useNavigation();
  const { formatInTimezone } = useTimezone();

  return <Table navigateTo={navigateTo} formatInTimezone={formatInTimezone} />;
};

export const Table = ({
  navigateTo,
  formatInTimezone,
}: {
  navigateTo: (route: string, state: object) => void;
  formatInTimezone: (date?: string | Date | undefined, dateFormat?: string | undefined) => string;
}) => {
  const [searchParams] = useSearchParams();
  const { pageParams, handlePagination, resetParams } = useInfiniteTableScroll();
  const { organizers, urlSearchParams, isFetching, fetchOrganizers } = useOrganizers();
  const tableHeightOffset = variables.headingHeights.withTitleTabs + 6;

  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;

      return;
    }
    resetParams();
  }, [urlSearchParams.searchValue, urlSearchParams.showDeactivated]);

  useEffect(() => {
    fetchOrganizers({
      ...pageParams,
      q: urlSearchParams.searchValue,
      deactivated: urlSearchParams.showDeactivated === 'true',
    });
  }, [pageParams]);

  return (
    <DataTableContainer heightOffset={tableHeightOffset} minWidth="1158px">
      <InfiniteScroll
        itemCount={organizers.length}
        pageNumber={pageParams.page}
        perPage={pageParams.perPage}
        handleNextPage={handlePagination}
      >
        <DataTable
          isLoading={isFetching}
          handleRowClick={({ uuid }) => navigateTo(`/settings/organizers/${uuid}`, { query: searchParams.toString() })}
          rows={organizers}
          noRecordsMessage={getNoOrganizersMessage()}
          actions={[
            {
              icon: 'baseline-edit-note',
              tooltip: 'Edit Organizer',
              iconSetter: () => 'baseline-edit-note',
              handleActionClick: ({ uuid }) =>
                navigateTo(`/settings/organizers/${uuid}`, { query: searchParams.toString() }),
            },
          ]}
          columns={[
            {
              label: 'Name',
              valueGetter: (details: Organizer) => (
                <div data-testid="fullname">{`${details.firstName} ${details.lastName}`}</div>
              ),
              align: 'left',
            },
            { key: 'email', align: 'left', label: 'Email' },
            {
              key: 'mobilePhoneNumber',
              label: 'Phone',
              align: 'left',

              valueSetter: (cellValue: string) => formatPhoneParenthesis(cellValue),
            },
            {
              key: 'roles',
              label: 'Role',
              align: 'left',

              valueSetter: (cellValue: string[]) => startCase(cellValue[0].replaceAll('_', ' ')),
            },
            {
              key: 'lastLogin',
              label: 'Last Login',
              align: 'left',

              valueSetter: (cellValue) => cellValue && formatInTimezone(cellValue, 'yyyy-MM-dd'),
            },
            { key: 'status', align: 'left', label: 'Status', valueSetter: (cellValue) => <Label state={cellValue} /> },
          ]}
        />
      </InfiniteScroll>
    </DataTableContainer>
  );
};

const Label = ({ state }: { state: string }) => {
  switch (state) {
    case 'active':
      return <SimpleLabel label={startCase(state)} textColor="text" bgColor="light-green" />;
    default:
      return <SimpleLabel label={startCase(state.replaceAll('_', ' '))} textColor="text" bgColor="neutralVariant90" />;
  }
};

const getNoOrganizersMessage = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
      <Text textColor="secondaryText" noWrap>
        No users matched your search
      </Text>
    </Box>
  );
};
